import { useId, useState, type ReactNode, type RefObject } from "react";

/**
 * A page's "On this page" rail in the documentation library (#534): beside the
 * text, sticky under the frozen page header, once the library has 76rem of
 * room (the `@container/docs` DocsLayout sets); with less, a button above
 * the text that opens the same list. The room is the container's, not the
 * window's, because the side rail takes 64 or 240px of the same window: at
 * 1280 wide with the rail open, a third column left the article 416px.
 *
 * Which of the two shows is CSS alone, so there is no width threshold to
 * keep in step in script. The caller's grid places it: first in the source,
 * so on a narrow screen the button sits above the text in reading and
 * keyboard order alike, and in the second column from 76rem.
 */
export function PageRail({ label, railRef, children }: { label: string; railRef: RefObject<HTMLElement>; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="mb-4 min-w-0 @min-[76rem]/docs:sticky @min-[76rem]/docs:top-[calc(var(--page-bar)+1rem)] @min-[76rem]/docs:col-start-2 @min-[76rem]/docs:row-start-1 @min-[76rem]/docs:mb-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-9 w-full items-center justify-between rounded-lg border border-neutral-300 bg-white px-3 text-sm font-medium text-neutral-700 @min-[76rem]/docs:hidden dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
      >
        On this page
        <span aria-hidden="true" className={`transition-transform ${open ? "rotate-180" : ""}`}>
          ▾
        </span>
      </button>
      {/* Under the frozen page header, not behind it; never taller than the room left above the footer. */}
      <nav
        ref={railRef}
        id={id}
        aria-label={label}
        className={`${open ? "block" : "hidden"} mt-2 max-h-[60vh] overflow-y-auto rounded-lg border border-neutral-200 bg-white p-2 @min-[76rem]/docs:mt-0 @min-[76rem]/docs:block @min-[76rem]/docs:max-h-[calc(100vh-var(--page-bar)-1.5rem-var(--docs-below,1rem))] @min-[76rem]/docs:rounded-none @min-[76rem]/docs:border-0 @min-[76rem]/docs:bg-transparent @min-[76rem]/docs:p-0 dark:border-neutral-800 dark:bg-neutral-900 @min-[76rem]/docs:dark:bg-transparent`}
      >
        <p className="hidden text-xs font-semibold uppercase tracking-wider text-neutral-500 @min-[76rem]/docs:block">On this page</p>
        {children}
      </nav>
    </div>
  );
}
